/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { keysOf } from '../common';
import { EuiIcon } from '../icon';
import { EuiI18n } from '../i18n';
var statusToClassNameMap = {
  complete: 'euiTourStepIndicator--complete',
  incomplete: 'euiTourStepIndicator--incomplete',
  active: 'euiTourStepIndicator--active'
};
export var STATUS = keysOf(statusToClassNameMap);
export var EuiTourStepIndicator = ({
  className,
  number,
  status,
  ...rest
}) => {
  const classes = classNames('euiTourStepIndicator', status ? statusToClassNameMap[status] : undefined, className);
  let indicatorIcon;

  if (status === 'active') {
    indicatorIcon = <EuiI18n token="euiTourStepIndicator.isActive" default="active">
        {isActive => <EuiIcon type="dot" className="euiStepNumber__icon" aria-label={isActive} color="secondary" aria-current="step" />}
      </EuiI18n>;
  } else if (status === 'complete') {
    indicatorIcon = <EuiI18n token="euiTourStepIndicator.isComplete" default="complete">
        {isComplete => <EuiIcon type="dot" className="euiStepNumber__icon" aria-label={isComplete} color="subdued" />}
      </EuiI18n>;
  } else if (status === 'incomplete') {
    indicatorIcon = <EuiI18n token="euiTourStepIndicator.isIncomplete" default="incomplete">
        {isIncomplete => <EuiIcon type="dot" className="euiStepNumber__icon" aria-label={isIncomplete} color="subdued" />}
      </EuiI18n>;
  }

  return <EuiI18n token="euiTourStepIndicator.ariaLabel" default={({
    status
  }) => {
    return `Step ${number} ${status}`;
  }} values={{
    status
  }}>
      {ariaLabel => <li className={classes} aria-label={ariaLabel} {...rest}>
          {indicatorIcon}
        </li>}
    </EuiI18n>;
};
EuiTourStepIndicator.propTypes = {
  number: PropTypes.number.isRequired,
  status: PropTypes.oneOf(["complete", "incomplete", "active"]).isRequired,
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string
};

try {
  EuiTourStepIndicator.__docgenInfo = {
    description: '',
    displayName: 'EuiTourStepIndicator',
    methods: [],
    props: {
      number: {
        defaultValue: null,
        description: '',
        name: 'number',
        parent: {
          fileName: 'app/src/components/tour/tour_step_indicator.tsx',
          name: 'EuiTourStepIndicatorProps'
        },
        required: true,
        type: {
          name: 'number'
        }
      },
      status: {
        defaultValue: null,
        description: '',
        name: 'status',
        parent: {
          fileName: 'app/src/components/tour/tour_step_indicator.tsx',
          name: 'EuiTourStepIndicatorProps'
        },
        required: true,
        type: {
          name: 'enum',
          raw: '"complete" | "incomplete" | "active"',
          value: [{
            value: '"complete"'
          }, {
            value: '"incomplete"'
          }, {
            value: '"active"'
          }]
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        required: false,
        type: {
          name: 'string'
        }
      }
    },
    extendedInterfaces: ['EuiTourStepIndicatorProps', 'CommonProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes']
  };
} catch (e) {}